import { isProduction } from './environment-helper';

// Performance optimization helper functions - production only
class PerformanceHelper {
  // Image optimization and lazy loading
  static optimizeImages() {
    if (!isProduction()) return;

    const imageObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;

            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }

            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              img.removeAttribute('data-srcset');
            }

            img.classList.remove('lazy-loading');
            img.classList.add('lazy-loaded');

            observer.unobserve(img);
          }
        });
      },
      {
        rootMargin: '50px',
      },
    );

    document.querySelectorAll('img[data-src]').forEach((img) => {
      img.classList.add('lazy-loading');
      imageObserver.observe(img);
    });
  }

  // Critical CSS injection
  static injectCriticalCSS() {
    if (!isProduction()) return;

    const criticalCSS = `
      body { 
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin: 0;
        padding: 0;
        line-height: 1.6;
      }
      
      .hero-section {
        min-height: 50vh;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .loading-skeleton {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
      }
      
      @keyframes loading {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      
      .lazy-loading {
        opacity: 0;
        transition: opacity 0.3s;
      }
      
      .lazy-loaded {
        opacity: 1;
      }
    `;

    const style = document.createElement('style');
    style.textContent = criticalCSS;
    document.head.appendChild(style);
  }

  // Resource hints for better loading (only essential ones)
  static addResourceHints() {
    if (!isProduction()) return;

    const hints = [
      { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: 'anonymous' },
      // Remove unused preloads that cause warnings
    ];

    hints.forEach((hint) => {
      // Check if already exists
      const existingHint = document.querySelector(`link[rel="${hint.rel}"][href="${hint.href}"]`);
      if (existingHint) return;

      const link = document.createElement('link');
      Object.entries(hint).forEach(([key, value]) => {
        if (key === 'crossorigin') {
          link.crossOrigin = value;
        } else {
          link[key] = value;
        }
      });
      document.head.appendChild(link);
    });
  }

  // Service Worker registration (fixed for HTTPS requirement)
  static registerServiceWorker() {
    if (!isProduction() || !('serviceWorker' in navigator)) return;

    // Check if running on HTTPS
    if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
      console.log('Service Worker requires HTTPS');
      return;
    }

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('Service Worker registered:', registration);
      })
      .catch((error) => {
        console.log('Service Worker registration failed:', error);
      });
  }

  // Core Web Vitals monitoring (fixed - using native Performance API)
  static monitorCoreWebVitals() {
    if (!isProduction()) return;

    console.log('Core Web Vitals monitoring: Using native Performance API');

    if ('PerformanceObserver' in window) {
      try {
        // Monitor LCP
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];

          if (lastEntry && window.gtag) {
            window.gtag('event', 'web_vitals', {
              event_category: 'Performance',
              event_label: 'LCP',
              value: Math.round(lastEntry.startTime),
            });
          }
        });

        if (PerformanceObserver.supportedEntryTypes.includes('largest-contentful-paint')) {
          lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        }

        // Monitor FID
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const fidValue = entry.processingStart - entry.startTime;

            if (window.gtag) {
              window.gtag('event', 'web_vitals', {
                event_category: 'Performance',
                event_label: 'FID',
                value: Math.round(fidValue),
              });
            }
          });
        });

        if (PerformanceObserver.supportedEntryTypes.includes('first-input')) {
          fidObserver.observe({ entryTypes: ['first-input'] });
        }

        // Monitor CLS
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });

          if (window.gtag && clsValue > 0) {
            window.gtag('event', 'web_vitals', {
              event_category: 'Performance',
              event_label: 'CLS',
              value: Math.round(clsValue * 1000),
            });
          }
        });

        if (PerformanceObserver.supportedEntryTypes.includes('layout-shift')) {
          clsObserver.observe({ entryTypes: ['layout-shift'] });
        }
      } catch (error) {
        console.log('Performance Observer setup failed:', error);
      }
    }
  }

  // Bundle size monitoring
  static monitorBundleSize() {
    if (!isProduction()) return;

    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'navigation' && window.gtag) {
          window.gtag('event', 'bundle_performance', {
            event_category: 'Performance',
            dom_content_loaded: Math.round(entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart),
            load_complete: Math.round(entry.loadEventEnd - entry.loadEventStart),
          });
        }
      });
    });

    if (PerformanceObserver.supportedEntryTypes.includes('navigation')) {
      observer.observe({ entryTypes: ['navigation'] });
    }
  }

  // Image format optimization
  static generateResponsiveImageSrc(imagePath, alt = '') {
    if (!isProduction()) return imagePath;

    const baseUrl = 'https://www.coachingsearchengine.com';
    const imageName = imagePath.split('/').pop().split('.')[0];

    return {
      src: `${baseUrl}/assets/images/${imageName}.webp`,
      fallback: `${baseUrl}/assets/images/${imageName}.jpg`,
      srcSet: [
        `${baseUrl}/assets/images/${imageName}-320.webp 320w`,
        `${baseUrl}/assets/images/${imageName}-640.webp 640w`,
        `${baseUrl}/assets/images/${imageName}-1024.webp 1024w`,
        `${baseUrl}/assets/images/${imageName}-1920.webp 1920w`,
      ].join(', '),
      sizes: '(max-width: 320px) 320px, (max-width: 640px) 640px, (max-width: 1024px) 1024px, 1920px',
      alt,
      loading: 'lazy',
      decoding: 'async',
    };
  }

  // Initialize all performance optimizations (fixed)
  static initializePerformanceOptimizations() {
    if (!isProduction()) {
      console.log('Performance optimizations skipped - not in production');
      return;
    }

    console.log('Initializing performance optimizations...');

    // DOM ready optimizations
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.optimizeImages();
        this.addResourceHints();
      });
    } else {
      this.optimizeImages();
      this.addResourceHints();
    }

    // Critical optimizations (immediate)
    this.injectCriticalCSS();

    // Deferred optimizations
    setTimeout(() => {
      this.registerServiceWorker();
      this.monitorCoreWebVitals();
      this.monitorBundleSize();
    }, 1000);
  }
}

export default PerformanceHelper;
